import React, { useState, useEffect } from "react";
import Iframe from "react-iframe";
import Lightbox from "../../../static/Lightbox/src/Lightbox";
import ShareButton from "../../ShareButton/src";
import {
  formatDate,
  formatNewsStoryItems,
  setResponsiveImage,
  mediaSizes,
  getMediaSize,
  resizeVideos,
  debounce,
  isSpecialEvent,
  isNewsArticle
} from "../../../helpers";
import "./index.scss";
import OlympicsVideoPlayer from "../../../static/OlympicsVideoPlayer";

interface socialMediaProps {
  showFacebook?: boolean;
  showTwitter?: boolean;
  showEmail?: boolean;
}

interface HeroDetail {
  label: string;
  value: string;
}

function VideoBackground(props: {
  preheaderLink?: string;
  preheaderLinkText?: string;
  preheaderLinkTarget?: string;
  isOlympics?: boolean;
  heroDetails?: HeroDetail[];
  videoId?: string;
  prevStory?: any;
  nextStory?: any;
  backgroundImageURL?: string;
  title?: string;
  socialMedia?: socialMediaProps;
  imageCredit?: string;
  imageTitle?: string;
  logoURL?: string;
  mobileImage?: string;
  tabletImage?: string;
  desktopImage?: string;
  heroTitle?: string;
  heroLink?: string;
  heroLinkText?: string;
  heroText?: string;
  heroSubtext?: string;
  heroMode?: boolean;
  isShort?: boolean;
  isGradient?: boolean;
  align?: "right" | "left";
  duration?: number | string;
  startTime?: number | string;
  disable?: boolean;
  externalLink?: {
    text: string;
    url: string;
  };
  curved?: boolean;
}) {
  const data = formatNewsStoryItems(props);
  const {
    isOlympics,
    videoId,
    prevStory,
    nextStory,
    title,
    socialMedia = [],
    imageCredit,
    imageTitle,
    mobileImage,
    tabletImage,
    desktopImage,
    heroTitle,
    heroLink = "",
    heroMode,
    heroLinkText,
    heroText,
    heroSubtext,
    isGradient,
    isShort,
    align = "left",
    logoURL,
    duration = 10,
    startTime = 0,
    externalLink,
    disable,
    preheaderLink,
    preheaderLinkText,
    preheaderLinkTarget
  } = data;
  const { heroDetails, curved = isSpecialEvent || isNewsArticle } = props;
  let [showVideo, showVideoToggle] = useState(true);
  let [fadeImage, setFadeImage] = useState(false);
  let [lightboxActive, showLightbox] = useState(false);
  const notMobile = getMediaSize(window.innerWidth) >= mediaSizes.mobile;
  const [videoPlaying, setVideoPlaying] = useState(false);

  const VideoHeroText = () => (
    <a
      target={heroLink.indexOf(location.host) !== -1 ? "" : "_blank"}
      className={!heroLink && !videoId ? "disable-click" : ""}
      href={heroLink ? heroLink : "javascript:void(0)"}
      onClick={
        !heroLink && videoId
          ? () => {
              showLightbox(true);
            }
          : () => {}
      }
    >
      <div className="landing-page__tile-bg">
        <div className="container">
          <div className="landing-page__tile-bg-text">
            {preheaderLink ? (
              <a
                className="landing-page__tile-bg-text-link invert no-icon white"
                href={preheaderLink}
                target={preheaderLinkTarget}
              >
                {preheaderLinkText}
              </a>
            ) : (
              <span>{heroSubtext}</span>
            )}
            <strong>{heroTitle}</strong>
            <p>{heroText}</p>
            {heroLinkText && (videoId || heroLink) ? (
              <div className="landing-page__tile-bg-text-link">
                {heroLinkText}
              </div>
            ) : null}
          </div>
          {videoId ? (
            <button
              onClick={e => {
                e.preventDefault();
                showLightbox(true);
                setFadeImage(true);
                if (showVideo) showVideoToggle(false);
              }}
              className="btn--video-player btn--video-player-large"
            />
          ) : null}
        </div>
      </div>
    </a>
  );

  const VideoHeroDetails = (props: {
    heroDetails: HeroDetail[];
    isMobile?: boolean;
  }) => {
    const { heroDetails, isMobile } = props;
    return (
      <div
        className={`video-hero__details ${
          isMobile ? "video-hero__details--mobile" : ""
        }`}
      >
        <div className="container">
          {heroDetails.map(heroDetail => {
            let output = null;
            if (heroDetail.value !== "") {
              output = (
                <div className="video-hero__details-item">
                  <span className="video-hero__label">{heroDetail.label}:</span>
                  <span className="video-hero__value">{heroDetail.value}</span>
                </div>
              );
            }
            return output;
          })}
        </div>
      </div>
    );
  };

  const VideoStories = () => {
    let [MouseOverActive, setMouseOverActive] = useState(false);

    const handleMouseOver = () => setMouseOverActive(true);
    const handleMouseOut = () => setMouseOverActive(false);

    const checkSocial = (socialMedia: any) => {
      return Object.keys(socialMedia).some(channel => socialMedia[channel]);
    };

    return (
      <div className={`container${MouseOverActive ? " mouseover-active" : ""}`}>
        {checkSocial(socialMedia) ? (
          <ShareButton disable={disable} socialMedia={socialMedia} />
        ) : null}
        {prevStory ? (
          <a
            href={prevStory.link}
            className="video-background__prev-btn"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            <p className="video-background__prev-btn-title">
              <span>Previous Story</span>
            </p>
            <div className="video-background__prev-story">
              <div className="video-background__prev-story-image">
                <div
                  style={{ backgroundImage: `url("${prevStory.imageUrl}")` }}
                />
              </div>
              <p className="video-background__prev-story-press">
                Press Release
              </p>
              <p className="video-background__prev-story-date">
                {formatDate(prevStory.date).shortNoComma}
              </p>
              <p className="video-background__prev-story-title">
                {prevStory.title}
              </p>
            </div>
          </a>
        ) : null}
        {videoId ? (
          isOlympics ? null : (
            <button
              onClick={() => {
                showLightbox(true);
                setFadeImage(true);
                if (showVideo) showVideoToggle(false);
              }}
              className="btn--video-player btn--video-player-large"
            />
          )
        ) : null}
        {nextStory ? (
          <a
            href={nextStory.link}
            className="video-background__next-btn"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
          >
            <p className="video-background__next-btn-title">
              <span>Next Story</span>
            </p>
            <div className="video-background__next-story">
              <div className="video-background__next-story-image">
                <div
                  style={{ backgroundImage: `url("${nextStory.imageUrl}")` }}
                />
              </div>
              <p className="video-background__prev-story-press">
                Press Release
              </p>
              <p className="video-background__prev-story-date">
                {formatDate(nextStory.date).shortNoComma}
              </p>
              <p className="video-background__next-story-title">
                {nextStory.title}
              </p>
            </div>
          </a>
        ) : null}
        <div className="video-background__title">
          <span>{title}</span>
          <div className="video-background__image-credits">
            {imageCredit ? <p>Credit: {imageCredit}</p> : null}
            {imageTitle ? <p>{imageTitle}</p> : null}
          </div>
        </div>
      </div>
    );
  };

  const VideoEl = ({
    mute = false,
    fade = false,
    lightbox = false
  }: {
    mute?: boolean;
    name?: string;
    fade?: boolean;
    lightbox?: boolean;
  }) => {
    let [fadeOut, setFade] = useState(false);
    useEffect(() => {
      debounce({
        func: () =>
          resizeVideos({ ElClassName: "video-background__video-container" })
      });
      resizeVideos({ ElClassName: "video-background__video-container" });

      let timer = setTimeout(() => {
        setFade(true);
        setTimeout(() => {
          showVideoToggle(false);
          setTimeout(
            () => {
              setFadeImage(true);
              setVideoPlaying(false);
            },
            duration ? parseInt(duration) * 100 : 100
          );
        }, 500);
      }, duration * 1000);

      if (!fade || !notMobile) clearTimeout(timer);

      return () => clearTimeout(timer);
    }, []);

    return (
      <div
        className={`video-background__video-container ${
          fadeOut ? "hide" : "show"
        } ${
          lightbox ? "video-background__video-container--lightbox" : ""
        } iframe`}
      >
        {!isOlympics ? (
          <Iframe
            referrerpolicy="strict-origin-when-cross-origin"
            url={`https://www.youtube.com/embed/${videoId}?loop=1&autoplay=1&origin=${
              location.href
            }${mute ? "&mute=1" : ""}${startTime ? `&t=${startTime}` : ""}`}
            width="100%"
            height="100%"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        ) : (
          <OlympicsVideoPlayer videoId={videoId} wrap={!isOlympics} />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (showVideo && videoId && notMobile && !isOlympics) {
      setVideoPlaying(true);
    }
  }, []);

  useEffect(() => {
    setResponsiveImage({
      mobileImage,
      tabletImage,
      desktopImage,
      elClassName: `video-background__image`
    });
  });

  const VideoBackgroundInner = () => (
    <div
      className={`video-background${!socialMedia ? " no-share" : ""}${
        heroMode ? " heroMode" : ""
      }${isShort ? " short" : ""} ${align}${isGradient ? " gradient" : ""}${
        videoPlaying ? " video-background--playing" : ""
      }${curved ? " curved" : ""}`}
    >
      {logoURL ? (
        <div
          className="video-background__tour-logo"
          style={{ backgroundImage: `url("${logoURL}")` }}
        />
      ) : null}
      {heroMode ? <VideoHeroText /> : <VideoStories />}
      {externalLink && externalLink.text !== "" ? (
        <div
          className={`video-background__external-link-container ${
            heroMode ? "video-background__external-link-container--hero" : ""
          }`}
        >
          <div className="container">
            <a
              className="video-background__external-link"
              href={externalLink.url}
            >
              {externalLink.text}
            </a>
          </div>
        </div>
      ) : null}

      {heroDetails ? <VideoHeroDetails heroDetails={heroDetails} /> : null}

      <div className={`video-background__video`}>
        {showVideo && videoId && notMobile && !isOlympics ? (
          <VideoEl fade={true} mute={true} />
        ) : (
          <div
            className={`video-background__image ${
              fadeImage || !videoId || !notMobile ? "show" : "hide"
            }`}
          />
        )}
        <div className="container" style={{ position: "absolute", bottom: 0 }}>
          <div
            className={`video-background__image-credits${
              fadeImage || !videoId || !notMobile ? "" : " hide"
            }`}
          >
            {imageCredit ? <p>Credit: {imageCredit}</p> : null}
            {imageTitle ? (
              <p>
                {imageTitle
                  .replace(/\b\d+\b|\(\d+\)/g, "")
                  .replace(/\s+/g, " ")
                  .trim()}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {curved ? (
        <div className="video-background__wrapper">
          <VideoBackgroundInner />
        </div>
      ) : (
        <VideoBackgroundInner />
      )}
      {heroDetails ? (
        <VideoHeroDetails heroDetails={heroDetails} isMobile />
      ) : null}
      <Lightbox
        show={lightboxActive}
        callback={() => {
          showLightbox(false);
        }}
      >
        <VideoEl lightbox={true} />
      </Lightbox>
    </>
  );
}

export default VideoBackground;
